import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  HerokuPlatformApiApp,
  HerokuPlatformApiPipeline,
  HerokuPlatformApiPipelineCoupling,
} from '@heroku-cli/typescript-api-schema';
import { HerokuApi } from '@internal/backstage-plugin-heroku-react';

/**
 * Default implementation of the HerokuAPI
 */
export class HerokuApiClient implements HerokuApi {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  async getApp(appId: string): Promise<HerokuPlatformApiApp> {
    return await this.fetch<HerokuPlatformApiApp>(`/apps/${appId}`);
  }

  async getApps(pipelineId: string): Promise<HerokuPlatformApiApp[]> {
    const pipelinesCouplings = await this.listPipelineCouplings(pipelineId);
    const appPromises = pipelinesCouplings.map(
      async (pc: HerokuPlatformApiPipelineCoupling) => {
        if (pc.app?.id) {
          return this.getApp(pc.app.id);
        }

        return null;
      },
    );

    // Not-null filter with type check
    const isNotNull = (
      x: HerokuPlatformApiApp | null,
    ): x is NonNullable<HerokuPlatformApiApp> => !!x;
    // Filter out null results
    const results: NonNullable<HerokuPlatformApiApp>[] = (
      await Promise.all(appPromises)
    ).filter(isNotNull);

    return results;
  }

  async getPipeline(pipelineId: string): Promise<HerokuPlatformApiPipeline> {
    return this.fetch<HerokuPlatformApiPipeline>(`/pipelines/${pipelineId}`);
  }

  async listPipelineCouplings(
    pipelineId: string,
  ): Promise<HerokuPlatformApiPipelineCoupling[]> {
    return this.fetch<HerokuPlatformApiPipelineCoupling[]>(
      `/pipelines/${pipelineId}/pipeline-couplings`,
    );
  }

  /**
   * Sends the Heroku API request through the backstage proxy and returns the
   * response as a typed object.
   * @param input The Heroku API Url to fetch from
   * @returns
   */
  private async fetch<T = any>(input: string): Promise<T> {
    const proxyUri = `${await this.discoveryApi.getBaseUrl(
      'proxy',
    )}/heroku-api`;
    const resp = await this.fetchApi.fetch(`${proxyUri}${input}`);
    if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText} ${input}`);
    return await resp.json();
  }
}
